import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, ArrowRight, Award, Users, ChevronDown } from "lucide-react";
import { Button } from "../ui/button";
import { useBooking } from "../../context";

const HeroSection = () => {
  const { setShowBookingModal } = useBooking();
  const navigate = useNavigate();

  return (
    <section 
      id="hero" 
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background */}
      <div className="absolute inset-0 stars-bg" />
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-30"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1613039704499-1975f634d75f?w=1920&q=80')" }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/50 to-slate-950" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <p className="text-lg md:text-xl text-slate-400 mb-4" data-testid="hero-subtitle">
            Melbourne Mobile Detailing
          </p>
          
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black mb-6 leading-tight" data-testid="hero-headline">
            <span className="bg-gradient-to-r from-cyan-400 via-cyan-300 to-white bg-clip-text text-transparent">RainXpress</span>
          </h1>
          
          <p className="text-base md:text-lg text-slate-400 mb-10 max-w-2xl mx-auto" data-testid="hero-subheadline">
            We come to you.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <Button 
              onClick={() => setShowBookingModal(true)}
              className="btn-primary-glow text-slate-900 font-bold px-8 py-4 rounded-full text-lg flex items-center gap-2 justify-center"
              data-testid="hero-book-now-btn"
            >
              <Calendar className="w-5 h-5" />
              Book Now
            </Button>
            <Button 
              onClick={() => navigate('/services')}
              className="btn-secondary font-semibold px-8 py-4 rounded-full text-lg flex items-center gap-2 justify-center"
              data-testid="hero-view-services-btn"
            >
              View Services
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>

          {/* Unsure message */}
          <p className="text-slate-400 text-sm mb-8 max-w-xl mx-auto">
            Unsure of what to book or need an accurate price quotation with travel fee?{' '}
            <a href="tel:0478054648" className="text-cyan-400 hover:text-cyan-300 font-medium">Call</a> or{' '}
            <a href="sms:0478054648" className="text-cyan-400 hover:text-cyan-300 font-medium">text now</a> and we respond same day.
          </p>

          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-6 md:gap-8" data-testid="trust-badges">
            <div className="trust-badge">
              <Award className="w-5 h-5" />
              <span>Professional Equipment</span>
            </div>
            <div className="trust-badge">
              <Users className="w-5 h-5" />
              <span>Satisfied Customers</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <ChevronDown className="w-8 h-8 text-cyan-400/50" />
      </motion.div>
    </section>
  );
};

export default HeroSection;
